import React from 'react';
import { Spinner } from '../../components/UI';
import {
    Button,
    Paper,
    Box,
    Typography,
    IconButton,
    Tooltip,
    TablePagination,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    Divider,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { getUserToken } from '../../utils/helper';
import useHubManagement from './useHubManagement';
import { getHubMasterPermission } from '../../utils/permissions';
import BluedartHubModal from '../../components/Modals/BluedartHubModal';
import { HUB_MANAGEMENT_TABLE_COLUMNS } from './hub.helper';
import { HubTableCell, HubTableHeadCell } from './utility.component.hub';
import TableSearchBar from '../../components/TableSearchBar';
import HubModal from '../../components/Modals/HubModal';
import UploadPayEarnModal from '../../components/Modals/UploadPayEarnModal';

interface HubManagementPageProps {
    [key: string]: any;
}
const HubManagementPage: React.FC = ({
    heliosTrackingData = [],
    staffTrackAndTrace,
    ...props
}: HubManagementPageProps) => {
    //store values
    const token = getUserToken?.();
    const {
        handleRowClick,
        getColumnComponentValue,
        hubList,
        pagination,
        handleChangeRowsPerPage,
        onSearchTextChange,
        searchTextValue,
        loading,
        containerRef,
        styles,
        handleChangePage,
        closeHubModal,
        selectedHub,
        isOpen,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        permissions,
        refetchHubsData,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    } = useHubManagement(props);

    const hubMasterPermissions = getHubMasterPermission();
    const [addHubOpen, setAddHubOpen] = React.useState(false);

    const uploadUIData: any = {
        bulk_hub: {
            title: 'Bulk Hub Upload',
            type: 'bulk_hub',
            enumType: 'bulk_hub',
            buttonText: 'Upload Hubs',
        },
        vendors_to_hub: {
            title: 'Vendors to Hub Mapping',
            type: 'vendors_to_hub',
            enumType: 'vendors_to_hub',
            buttonText: 'Upload Vendors to Hubs',
        },
    };

    const hasHubs = !!hubList?.hubs?.length;

    return (
        <Spinner loading={loading}>
            {/* Toolbar: search is submitted explicitly, so typing never queries. */}
            <Box mb={1} className={styles.toolbar}>
                <TableSearchBar
                    placeholder="Search by hub name, city or store ID"
                    value={searchTextValue}
                    onChange={onSearchTextChange}
                    onSearch={handleSearchSubmit}
                    onClear={handleSearchClear}
                    isDirty={isSearchDirty}
                    hasApplied={!!appliedSearchText}
                    width={340}
                />

                <Box
                    ml="auto"
                    display="flex"
                    alignItems="center"
                    style={{ gap: 8 }}
                >
                    {hasHubs && (
                        <Typography className={styles.resultSummary}>
                            {`${hubList?.totalCount ?? 0} hubs`}
                        </Typography>
                    )}
                    <Tooltip title="Refresh" arrow>
                        <IconButton
                            size="small"
                            onClick={() => refetchHubsData?.()}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {/* RB-8: hub master data is a Super Admin / Regional Approver capability. These
                        were gated on ops_management.update, which a Rider Approver also needs for
                        shift create/edit — one bit cannot express both, hence the hub_master key.
                        Hidden rather than disabled: a disabled control still advertises a
                        capability the backend will refuse. */}
                    {hubMasterPermissions.update && (
                        <>
                            <Button
                                variant="outlined"
                                className={styles.outlinedButton}
                                onClick={(e) => {
                                    openBulkUploadModal?.('vendors_to_hub');
                                }}
                                disableElevation
                            >
                                {'Bulk Map Vendors to Hubs'}
                            </Button>
                            <Button
                                variant="outlined"
                                className={styles.outlinedButton}
                                onClick={(e) => {
                                    openBulkUploadModal?.('bulk_hub');
                                }}
                                disableElevation
                            >
                                {'Bulk Upload Hubs'}
                            </Button>
                            {/* RB-9: single-add for one-off hubs; only bulk existed before. */}
                            {hubMasterPermissions.create && (
                                <Button
                                    variant="contained"
                                    className={styles.primaryButton}
                                    onClick={() => setAddHubOpen(true)}
                                    disableElevation
                                >
                                    {'Add Hub'}
                                </Button>
                            )}
                        </>
                    )}
                </Box>
                {addHubOpen && (
                    <BluedartHubModal
                        open
                        onClose={() => setAddHubOpen(false)}
                        onSuccess={() => refetchHubsData?.()}
                    />
                )}
                {!!openBulkModal && bulkUploadType && (
                    <UploadPayEarnModal
                        open={openBulkModal}
                        data={{}}
                        isEdit={false}
                        enumType={bulkUploadType}
                        onClose={() => {
                            closeBulkUploadModal?.();
                        }}
                        uiData={
                            bulkUploadType
                                ? uploadUIData?.[bulkUploadType]
                                : undefined
                        }
                    />
                )}
            </Box>
            <Paper
                ref={containerRef}
                className={styles.container}
                elevation={0}
                style={{ padding: '1rem' }}
            >
                {hasHubs ? (
                    <>
                        <TableContainer
                            component={Box}
                            className={styles.tableContainer}
                        >
                            <Table stickyHeader aria-label="Hubs table">
                                <TableHead>
                                    <TableRow>
                                        {HUB_MANAGEMENT_TABLE_COLUMNS?.map(
                                            (column: any, index: any) => {
                                                return (
                                                    <HubTableHeadCell
                                                        key={`column${index}`}
                                                        align={
                                                            column?.align ??
                                                            'left'
                                                        }
                                                        style={{
                                                            ...(!!column?.minWidth && {
                                                                minWidth:
                                                                    column?.minWidth,
                                                            }),
                                                            ...(!!column?.width && {
                                                                width: column?.width,
                                                            }),
                                                        }}
                                                    >
                                                        {column?.name}
                                                    </HubTableHeadCell>
                                                );
                                            },
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {hubList?.hubs?.map(
                                        (row: any, rowIndex?: number) => (
                                            <React.Fragment
                                                key={
                                                    row?.id ??
                                                    row?.name ??
                                                    rowIndex
                                                }
                                            >
                                                <TableRow
                                                    className={styles.tableRow}
                                                    onClick={(e: any) => {
                                                        handleRowClick(e, row);
                                                    }}
                                                >
                                                    {HUB_MANAGEMENT_TABLE_COLUMNS?.map(
                                                        (
                                                            column: any,
                                                            index: any,
                                                        ) => {
                                                            return (
                                                                <HubTableCell
                                                                    key={`row${index}`}
                                                                    align={
                                                                        column?.align ??
                                                                        'left'
                                                                    }
                                                                    style={{
                                                                        ...(!!column?.width && {
                                                                            maxWidth:
                                                                                column?.width,
                                                                        }),
                                                                        ...(!!column?.minWidth && {
                                                                            minWidth:
                                                                                column?.minWidth,
                                                                        }),
                                                                    }}
                                                                >
                                                                    {getColumnComponentValue(
                                                                        row,
                                                                        column,
                                                                        null,
                                                                    )}
                                                                </HubTableCell>
                                                            );
                                                        },
                                                    )}
                                                </TableRow>
                                                <Divider
                                                    style={{
                                                        margin: '4px 0',
                                                        backgroundColor: '#fff',
                                                    }}
                                                />
                                            </React.Fragment>
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box className={styles.paginationContainer}>
                            <TablePagination
                                rowsPerPageOptions={
                                    hubList?.hubs?.length > 25 ||
                                    hubList?.totalCount > 25
                                        ? [25, 50, 75, 100]
                                        : []
                                }
                                component="div"
                                count={hubList?.totalCount ?? 0}
                                rowsPerPage={pagination?.pageSize}
                                page={pagination?.pageNo}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Box className={styles.emptyState}>
                        <Typography className={styles.emptyTitle}>
                            No hubs found
                        </Typography>
                        <Typography className={styles.emptySubtitle}>
                            {!!appliedSearchText
                                ? `Nothing matches “${appliedSearchText}”.`
                                : 'There are no hubs to show yet.'}
                        </Typography>
                        {!!appliedSearchText && (
                            <Box mt={1}>
                                <Button
                                    className={styles.ghostButton}
                                    onClick={handleSearchClear}
                                >
                                    Clear search
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
            {!!isOpen && (
                <HubModal
                    open={isOpen}
                    defaultValue={selectedHub}
                    onClose={() => {
                        closeHubModal?.();
                    }}
                />
            )}
        </Spinner>
    );
};
export default HubManagementPage;
